// MarkdownCell — minimal static-cell loader for Subscribe step bodies.
//
// Distinct from CellHost (which routes through the agent + has its own
// header chrome). MarkdownCell:
//   - Fetches a static markdown path under /content/*
//   - Interpolates simple $-bindings client-side (just subscriptionId
//     for Phase 2.4b — full BindingContext is Phase 6 agent territory)
//   - Mounts MarkdownRenderer with no surrounding chrome
//
// The host component owns the surrounding chrome (Subscribe's
// SubscribeShell provides Crumbs + PageHead + Stepper).

import { useEffect, useState } from 'react';
import { MarkdownRenderer } from '@rmint/shell';
import { api } from '@rmint/shell';

export type MarkdownCellProps = {
  /** Path under /content/, e.g. 'subscribe/kyc.pre.md'. */
  path: string;
  /** Bindings interpolated into $-prefixed placeholders. */
  bindings?: Record<string, string>;
  /** Wallet address forwarded to MarkdownRenderer for widgets. */
  walletAddress?: string;
};

type State =
  | { status: 'loading' }
  | { status: 'ok'; markdown: string }
  | { status: 'not-found' }
  | { status: 'error'; detail: string };

function applyBindings(markdown: string, bindings: Record<string, string>): string {
  let out = markdown;
  // Sort longest-first so $subscription.id replaces before any $subscription token.
  const keys = Object.keys(bindings).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const value = bindings[key]!;
    out = out.split(`$${key}`).join(value);
  }
  return out;
}

export function MarkdownCell({ path, bindings = {}, walletAddress }: MarkdownCellProps) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    const fullPath = path.startsWith('/') ? path : `/content/${path}`;
    api
      .get(fullPath, { responseType: 'text' })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setState({ status: 'not-found' });
          return;
        }
        if (res.status >= 400) {
          setState({ status: 'error', detail: `HTTP ${res.status}` });
          return;
        }
        const text = typeof res.data === 'string' ? res.data : '';
        setState({ status: 'ok', markdown: applyBindings(text, bindings) });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ status: 'error', detail: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(bindings)]);

  if (state.status === 'loading') {
    return (
      <div className="space-y-3" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-4 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }
  if (state.status === 'not-found') {
    return (
      <div role="status" className="rounded-card border border-border bg-muted p-6 text-sm text-foreground-mute">
        Cell template not found at <code className="font-mono">{path}</code>.
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div role="alert" className="rounded-card border border-danger-line bg-danger-bg p-4 text-sm text-danger">
        Failed to load cell: {state.detail}
      </div>
    );
  }
  return (
    <article data-testid="markdown-cell-body">
      <MarkdownRenderer walletAddress={walletAddress}>{state.markdown}</MarkdownRenderer>
    </article>
  );
}
